import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { Slide } from '@components/animators/slide';
import { HorizontalTabs } from '@components/horizontal-tabs';
import { IconButton } from '@components/icon-button';
import { IconCloseRegular } from '@components/icons/close-regular';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { conditionsTab } from './conditions-tab';
import { useFormBuilderContext } from './context';
import { getFieldConfigForKind } from './field-registry';
import { optionsTab } from './options-tab';

export type FieldPropertiesProps = {};

export const FieldProperties = () => {
  const {
    store: { useSelectedField },
  } = useFormBuilderContext();
  const selectedField = useSelectedField();
  const config = selectedField ? getFieldConfigForKind(selectedField.kind) : null;
  const properties = config?.propertiesTabs ?? [];
  const tabs = selectedField
    ? properties
        .filter((config) => {
          switch (config) {
            case optionsTab:
              return selectedField.builderProps?.canEditOptions ?? true;
            case conditionsTab:
              return selectedField.builderProps?.canEditConditions ?? true;
            default:
              return true;
          }
        })
        .map((config) => config(selectedField))
    : [];

  return (
    <AnimatePresence initial={false}>
      {selectedField && tabs.length > 0 && (
        <RightSidebar id="right-sidebar">
          <AnimatePresence initial={false}>
            <Slide key={selectedField.id} offset={20}>
              <TopContainer>
                <Stack space={4}>
                  <ElementPropertiesHeader />
                  <NegativePad>
                    <HorizontalTabs key={tabs.map((tab) => tab.label).join(',')} items={tabs} />
                  </NegativePad>
                </Stack>
              </TopContainer>
            </Slide>
          </AnimatePresence>
        </RightSidebar>
      )}
    </AnimatePresence>
  );
};

const ElementPropertiesHeader = () => {
  const {
    store: { actions },
  } = useFormBuilderContext();
  const { t } = useScopedTranslate(locale);
  return (
    <Stack orientation="horizontal" space={2} justify="space-between" align="center">
      <Text font="bold18" text={t('properties')} />
      <IconButton
        dataTestid="close-button"
        variant="ghost-purple"
        icon={<IconCloseRegular />}
        onClick={actions.unselect}
        style={{ margin: pxToRem(-8) }}
      />
    </Stack>
  );
};

const locale = {
  en: {
    properties: 'Element properties',
  },
};

const RightSidebar = styled(motion.div).attrs({
  initial: { opacity: 0, x: '100%' },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: '20%', transition: { duration: 0.1 } },
  transition: { type: 'just' },
})({
  background: theme.color.backgroundLightDark,
  overflowY: 'auto',
  boxShadow: `${pxToRem(1)} 0px 0px 0px ${theme.color.border10} inset`,
});

const TopContainer = styled.div({
  padding: pxToRem(24),
});

const NegativePad = styled.div({
  margin: '0 -1.5rem',
});
